import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { Check } from 'lucide-react'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { Cta } from '@/components/generals/cta'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'
import { LINKS } from '@/constants/site'

interface SwitchBandProps {
    index?: string
    title?: string
    text: string
    points: string[]
    from?: string
    id?: string
}

/**
 * "Ingen grunn til byttefrykt": a big switch flips from the old system to Svenn
 * as the band scrolls through the viewport.
 */
export const SwitchBand: FC<SwitchBandProps> = ({
    index,
    title = 'Ingen grunn til byttefrykt',
    text,
    points,
    from = 'Dagens system',
    id = 'bytte'
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const knob = useRef<HTMLDivElement>(null)

    useScrollProgress(ref, {
        startAt: 0.9,
        endAt: 0.55,
        varName: null,
        onUpdate: progress => {
            const k = Math.min(1, Math.max(0, (progress - 0.15) / 0.45))
            const el = knob.current

            if (!el) return

            el.style.setProperty('--k', k.toFixed(4))
            el.dataset.on = k > 0.5 ? 'true' : 'false'
        }
    })

    return (
        <section ref={ref} id={id} className='tone-butter relative overflow-hidden py-24 md:py-36' data-header='light'>
            <div className='wrap grid grid-cols-12 items-center gap-x-6 gap-y-16'>
                <div className='col-span-12 lg:col-span-6'>
                    <SectionLabel index={index}>Bytt uten å betale dobbelt</SectionLabel>

                    <h2 className='t-h1 mt-6 max-w-[12ch] balance' data-reveal='words'>
                        <SplitWords text={title} />
                    </h2>

                    <div
                        ref={knob}
                        className='switch mt-14'
                        data-on='false'
                        style={{ '--k': 0 } as CSSProperties}
                        aria-hidden='true'
                    >
                        <div className='switch-track'>
                            <div className='switch-knob'>
                                <span className='switch-knob-mark' />
                            </div>
                        </div>
                        <div className='switch-labels t-label'>
                            <span>{from}</span>
                            <span>Svenn</span>
                        </div>
                    </div>
                </div>

                <div className='col-span-12 lg:col-span-5 lg:col-start-8'>
                    <p className='t-lead pretty'>{text}</p>

                    <ul className='mt-8 border-t border-tone-line'>
                        {points.map((point, i) => (
                            <li
                                key={point}
                                className='flex items-center gap-4 border-b border-tone-line py-4 font-semibold'
                                data-reveal='rise'
                                style={{ '--d': i } as CSSProperties}
                            >
                                <span className='flex size-7 shrink-0 items-center justify-center bg-night text-butter'>
                                    <Check className='size-4' strokeWidth={2.5} aria-hidden='true' />
                                </span>
                                {point}
                            </li>
                        ))}
                    </ul>

                    <div className='mt-10 flex flex-wrap gap-3'>
                        <Cta href={LINKS.signup} magnetic>Prøv 14 dager gratis</Cta>
                        <Cta href='/bli-oppringt' variant='ghost'>Bli oppringt</Cta>
                    </div>
                </div>
            </div>
        </section>
    )
}
